import React, { useState, useEffect } from 'react'
import * as Yup from 'yup'
import { Box } from '../../designSystem'
import {
  LoginButton,
  InstructionNote,
  InputContainer,
  SetupHeading,
  DoB,
  DateOfBirth,
  CautionNote,
  WhatAppInfoContainer,
  WhatAppCheckBox,
} from './styles'
import { Formik, Form } from 'formik'
import {
  FloatingInput,
  DropDownSelect,
  SearchableSingleSelectDropdown,
  PhoneInputField,
} from '../FormikInputs'
import {
  checkForGlobalLogin,
  handleEventLog,
  handleFacebookEvents,
  saveSession,
} from '../../lib/utils'
import Loader from '../Loader'
import { post } from '../../lib/api'
import { menuItemObj, countryList, languages } from '../../constants'
import WhatsappIcon from '../Images/WhatsappIcon'

function Register({
  email,
  type,
  message,
  setMessage,
  langPref,
  tempSessionInfo,
  history,
  easySignup,
}) {
  const [country, setCountry] = useState('')
  const [dob, setDoB] = useState('')
  const [whatsappConsent, setWhatsappConsent] = useState(true)
  const isMobile = window.screen.width < 600
  const referrer = localStorage.getItem('referrer')

  useEffect(() => {
    checkForGlobalLogin()
      .then((result) => {
        setCountry(result.countryCode)
      })
      .catch(() => {})
  }, [])

  return (
    <>
      <Formik
        initialValues={{
          fullName: '',
          email: email,
          phone_number: '',
          langPref: '',
          country: country || '',
          gender: '',
          referrer: '',
          experience: '',
        }}
        validationSchema={Yup.object({
          email: Yup.string().required('Required'),
          fullName: Yup.string().required('Required'),
          langPref: Yup.string().required('Required'),
          country: Yup.string().required('Required'),
          gender: Yup.string().required('Required'),
          referrer:
            (!referrer || referrer === 'url') &&
            Yup.string().required('Required'),
        })}
        onSubmit={ async (values, { setSubmitting }) => {
          if (values.langPref !== 'english') {
            setMessage({
              text: 'Thank you for your interest ; currently we are only open to english authors. We will reach out to you once we open the program in your language ',
              color: 'blue',
            })
            setSubmitting(false)
            return
          }
          handleEventLog(
            type === 'login' ? 'email_log_in' : 'email_sign_up',
            'button_click',
            { module_name: type === 'login' ? 'email_log_in' : 'email_sign_up' }
          )
          setMessage({ ...message, text: '' })
          const { uid, token } = tempSessionInfo || {}
          if (!uid) return

          // send username to update user api
          const userData = {
            fullname: values.fullName,
            view: 'writers',
            email: values.email,
            dob: dob,
            gender: values.gender,
            login_type: 'email_login',
            easy_signup: easySignup ? 1 : 0,
          }
          if (values.phone_number)
            userData.phone_number = `+${values.phone_number}`
          if (values.country !== 'Global') userData.location = values.country
          try {
            await post('/user_api/user.update?is_novel=1', {
              data: userData,
              headers: { uid, 'access-token': token },
            })
            const userAccountData = {
              account_type: 'Author',
              languages: values.langPref.toLowerCase(),
              fullname: values.fullName,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              source_platform: 'writers',
              device_id: localStorage.getItem('uuid'),
              experience_dropdown: values.experience,
              uid,
              whatsapp_notifications: whatsappConsent ? 1 : 0
            }
            if (values.country !== 'Global') userAccountData.location = values.country

            if (referrer && referrer !== 'url') {
              userAccountData.referrer = referrer
            } else {
              userAccountData.referrer = values.referrer
              let newReferrer = values.referrer.split(' ').join('_')
              localStorage.setItem('referrer', newReferrer)
            }

            let campaign = sessionStorage.getItem('campaign')
            let medium = sessionStorage.getItem('medium')

            if (campaign) {
              userAccountData.campaign = campaign
            }
            if (medium) {
              userAccountData.medium = medium
            }
            if (localStorage.getItem('source_app')) {
              userAccountData.source_app = localStorage.getItem('source_app')
            }
            if (localStorage.getItem('native_app_version')) {
              userAccountData.app_version_current = localStorage.getItem('native_app_version')
            }
            await post('/user_api/user.create_creator_account?is_novel=1', {
              data: userAccountData,
              headers: {
                uid,
                'access-token': token,
                'device-type': isMobile ? 'Mobile' : 'Desktop',
              },
            })
            setSubmitting(false)
            tempSessionInfo &&
              saveSession({
                ...tempSessionInfo,
                name: values.fullName,
              })
            handleFacebookEvents(false)
            if (referrer?.includes('contest'))
              window.location.href = `/shows/novels/?referrer=${referrer}&open-new=1`
            else history.push(menuItemObj.home)
          } catch (err) {
            setSubmitting(false)
            console.log(err)
            setMessage({ ...message, text: err.message, color: 'red' })
            throw err
          }
        }}
      >
        {({ values = {}, isSubmitting, errors }) => (
          <Form>
            <SetupHeading>
              Set up your Writer's Account !
            </SetupHeading>

            <Box mb={16}>
              <FloatingInput type="text" name="fullName" label="Pen Name" />
              <CautionNote>
                Note - This name will be shown & used in your writer profile & content
              </CautionNote>
            </Box>

            {!email && (
              <Box mb={16}>
                <FloatingInput
                  type="text"
                  name="email"
                  label="Your Email"
                  disabled
                />
              </Box>
            )}
            <Box mt={16}>
              <PhoneInputField
                name="phone_number"
                type="text"
                country="us"
                style={{ margin: '0' }}
              />
            </Box>
            <WhatAppInfoContainer>
              <WhatAppCheckBox
                type="checkbox"
                defaultChecked
                value={whatsappConsent}
                onChange={(e) => setWhatsappConsent(e.target.checked)}
              />
              Get important updates on <WhatsappIcon /> Whatsapp
            </WhatAppInfoContainer>
            <InstructionNote>
              Swift assistance, no spam, always private.
            </InstructionNote>
            <InputContainer>
              <Box mt={16}>
                <SearchableSingleSelectDropdown
                  label="Language"
                  name="langPref"
                  selectKey="id"
                  nameKey="name"
                  placeholder="Type to Search"
                  valueArray={languages}
                />
              </Box>
              {values.langPref && values.langPref === 'english'  && (
                <InstructionNote>
                  Note: We will show your book to{' '}
                  <strong>{values.langPref}</strong> audience{' '}
                </InstructionNote>
              )}
              {values.langPref && values.langPref !== 'english'  && (
                <Box
                  color='blue'
                >Currently we are accepting audio series only in English language. 
                </Box>
              )}
              <Box mt={16}>
                <SearchableSingleSelectDropdown
                  label="Country"
                  name="country"
                  selectKey="code"
                  nameKey="name"
                  placeholder="Type to Search"
                  valueArray={countryList}
                />
              </Box>
              <Box mt={16}>
                <DropDownSelect
                  name="gender"
                  placeholder="Click to select Gender"
                  selectOptions={[
                    { id: 'male', name: 'Male' },
                    { id: 'female', name: 'Female' },
                    { id: 'others', name: 'Others' },
                  ]}
                  idKey="id"
                  nameKey="name"
                />
              </Box>
              <DateOfBirth>
                <label>Date of Birth</label>
                <DoB
                  type="date"
                  value={dob}
                  onChange={(e) => setDoB(e.target.value)}
                  placeholder="Date of Birth"
                  max={new Date().toISOString().split('T')[0]}
                />
              </DateOfBirth>
              {(!referrer || referrer === 'url') && (
                <Box mt={16}>
                  <DropDownSelect
                    name="referrer"
                    placeholder="What inspired you to write here?"
                    selectOptions={[
                      { name: 'Saw while listening shows' },
                      { name: 'Friend/Colleague told me' },
                      { name: 'Saw on Youtube' },
                      { name: 'Read a Blog' },
                      { name: 'Other' },
                    ]}
                    idKey="name"
                    nameKey="name"
                  />
                </Box>
              )}
              <Box mt={16}>
                <DropDownSelect
                  name="experience"
                  placeholder="Your Experience"
                  selectOptions={[
                    { name: 'I mostly write for fun' },
                    { name: 'I’m exploring to submit my published work' },
                    { name: 'I’m pursuing a career as a writer' },
                    { name: 'I am professional writer' },
                    { name: 'None of above' },
                  ]}
                  idKey="name"
                  nameKey="name"
                />
              </Box>
              <Box mt={16}>
                <LoginButton
                  id="login-handler"
                  disabled={Object.keys(errors).length > 0}
                  type="submit"
                >
                  {isSubmitting ? (
                    <Loader size="sm" invertColor noPadding />
                  ) : (
                    'continue'
                  )}
                </LoginButton>
              </Box>
              {message.text !== '' && (
                <Box
                  variant="flex"
                  justifyContent="center"
                  color={message.color}
                >
                  {message.text}
                </Box>
              )}
            </InputContainer>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default Register